import {createSelector} from '@reduxjs/toolkit'

import {first} from '@shared/helpers/array'
import {includes} from '@shared/helpers/collection'
import {eq} from '@shared/helpers/lang'
import {someValueKeys} from '@shared/helpers/object'

import {
    APIStatus,
    ApiResult,
    ProductOrderStatus,
    ProductClass,
    ExposureStatus,
    DeliveryTagTypeValues,
} from '@web/core/constants'
import {isArrivalGuarantee, isKurlyOrder} from '@web/helpers/policies'

import {RootState} from './interfaces'
import {Assignment, AssignmentsByProductOrderNo} from './interfaces/assignment'
import {getAssignments} from './utils/product'

export const productDetailSelector = (state: RootState) => state.productDetail

export const productsSelector = (state: RootState) => state.productDetail.products

export const giveawaySelector = (state: RootState) => state.productDetail.giveawayProducts

export const quantityClaimsNoSelector = createSelector(productsSelector, (products) => {
    return products.reduce<string[]>((acc, product) => {
        return [...acc, ...(product.quantityClaims?.map((quantityClaim) => quantityClaim.claim.no) || [])]
    }, [])
})

export const productDetailInfoSelector = createSelector(productDetailSelector, ({products, productBundle}) => {
    const productsByPaymentWaiting = products.filter(
        ({status}) => status.orderType === ProductOrderStatus.PAYMENT_WAITING,
    )
    const productsByCanceledByNoPayment = products.filter(
        ({status}) => status.orderType === ProductOrderStatus.CANCELED_BY_NOPAYMENT,
    )
    const productsByPayDone = products.filter(({status}) => status.exposureType === ExposureStatus.결제완료)

    return {
        firstProduct: first(products),
        firstProductBundle: first(Object.keys(productBundle).map((key) => productBundle[key])),
        products,
        receiverCount: products.reduce<string[]>((acc, {giftingReceiver}) => {
            if (giftingReceiver && acc.indexOf(giftingReceiver.receiverName) < 0) {
                acc.push(giftingReceiver.receiverName)
            }
            return acc
        }, []).length,
        productQuantity: products.reduce((acc, {price}) => acc + (price?.orderQuantity ?? 0), 0),
        productOrderAmount: products.reduce((acc, {price}) => acc + (price?.orderAmount ?? 0), 0),
        /** 결제 대기중 상품목록 */
        productsByPaymentWaiting,
        /** 재구매 가능 상품 목록 */
        productsByRepurchase: products.filter(({status}) => status.classType !== ProductClass.SUPPLEMENTED),
        /** 결제 대기중 포함 여부 */
        hasPaymentWaiting: productsByPaymentWaiting.length > 0,
        /** 결제 완료 포함 여부 */
        hasPayDone: productsByPayDone.length > 0,
        hasCanceledByNoPayment: productsByCanceledByNoPayment.length > 0,
        hasDelivery:
            products.some(({status, delivery}) => status.isEcoupon || delivery?.attributeType === 'NORMAL') ||
            someValueKeys(({overSeaDelivery}) => overSeaDelivery, productBundle),
        hasOverseaWithArrivalGuarantee: products.every((product) => {
            const {bundleGroupKey, delivery} = product
            const bundle = productBundle[bundleGroupKey]
            return !isArrivalGuarantee(delivery?.attributeType) || !bundle?.overSeaDelivery
        }),
        hasKurlyProduct: products.some((product) => isKurlyOrder({branchMarketType: product.branch?.marketType})),
        hasConsentToTransferAbroadMerchant: products.some((product) => {
            const {bundleGroupKey} = product
            const bundle = productBundle[bundleGroupKey]

            return bundle?.isConsentToTransferAbroad ?? false
        }),
        /** deliveryTagType 합집합 */
        deliveryTagTypes: products.reduce((acc, product) => {
            const deliveryTag = product.delivery?.tagType

            if (deliveryTag && !includes(deliveryTag, acc)) {
                return [...acc, deliveryTag]
            }

            return acc
        }, [] as DeliveryTagTypeValues[]),
    }
})

export const assignmentsSelector = createSelector(
    productDetailSelector,
    ({assignments: {value, apiStatus, reloadCount}, products}) => {
        const eqAPIStatus = eq(apiStatus)
        const status = (() => {
            if (eqAPIStatus(APIStatus.RESOLVED)) {
                return APIStatus.RESOLVED
            } else if (eqAPIStatus(APIStatus.REJECTED) && reloadCount !== 0) {
                return ApiResult.FAILED_RETRY_AVAILABLE
            } else if (includes(apiStatus, [APIStatus.FETCHING, APIStatus.IDLE])) {
                return apiStatus
            }
            return ApiResult.FAILED
        })()

        return {
            apiStatus: status,
            assignments: Object.entries(value).reduce<AssignmentsByProductOrderNo>(
                (acc, [productOrderNo, rawAssignments]) => {
                    const product = products.find(({orderNo}) => orderNo === productOrderNo)
                    const exposureType = product?.status.exposureType
                    const deliveryKeeperRegistered = product?.delivery?.deliveryKeeperRegistered
                    const quantityClaims = product?.quantityClaims

                    const main = getAssignments({
                        rawAssignments: rawAssignments.main,
                        exposureType,
                        deliveryKeeperRegistered,
                    })

                    const claims = quantityClaims
                        ? Object.entries(rawAssignments.claims).reduce<Record<string, Assignment[]>>(
                              (acc2, [claimNo, claimAssignments]) => {
                                  const quantityClaim = quantityClaims.find(({claim}) => claim.no === claimNo)

                                  return {
                                      ...acc2,
                                      [claimNo]: getAssignments({
                                          rawAssignments: claimAssignments,
                                          exposureType: quantityClaim?.exposureType,
                                          deliveryKeeperRegistered,
                                      }),
                                  }
                              },
                              {},
                          )
                        : {}

                    return {
                        ...acc,
                        [productOrderNo]: {
                            main,
                            claims,
                        },
                    }
                },
                {},
            ),
            reloadCount,
        }
    },
)
